import React, { useState, useEffect } from 'react';
import { usePerformance } from '../contexts/PerformanceContext';
import { useAuth } from '../contexts/AuthContext';
import PerformanceReviewForm from './PerformanceReviewForm';
import LoadingSpinner from './LoadingSpinner';

const PerformanceReviewList = () => {
  const { 
    performanceReviews, 
    reviewCycles,
    loading, 
    fetchPerformanceReviews,
    submitSelfAssessment,
    submitManagerReview,
    createPerformanceReview,
    fetchReviewCycles
  } = usePerformance();
  const { user, hasAnyRole } = useAuth();
  
  const [showForm, setShowForm] = useState(false);
  const [currentReview, setCurrentReview] = useState(null);
  const [formType, setFormType] = useState('self'); // 'self' or 'manager'
  const [filters, setFilters] = useState({
    cycle_id: '',
    status: '',
    my_reviews: true
  });

  useEffect(() => {
    fetchPerformanceReviews(filters);
    if (reviewCycles.length === 0) {
      fetchReviewCycles();
    }
  }, [filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleStartSelfAssessment = (review) => {
    setCurrentReview(review);
    setFormType('self');
    setShowForm(true);
  };

  const handleStartManagerReview = (review) => {
    setCurrentReview(review);
    setFormType('manager');
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    let result;
    if (formType === 'self') {
      result = await submitSelfAssessment(currentReview.id, formData);
    } else {
      result = await submitManagerReview(currentReview.id, formData);
    }
    
    if (result.success) {
      setShowForm(false);
      setCurrentReview(null);
      fetchPerformanceReviews(filters);
    }
    
    return result;
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setCurrentReview(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      not_started: 'bg-gray-100 text-gray-800',
      self_assessment_pending: 'bg-yellow-100 text-yellow-800',
      manager_review_pending: 'bg-blue-100 text-blue-800',
      peer_feedback_pending: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getRatingColor = (rating) => {
    const colors = {
      exceptional: 'bg-green-100 text-green-800',
      exceeds_expectations: 'bg-blue-100 text-blue-800',
      meets_expectations: 'bg-yellow-100 text-yellow-800',
      below_expectations: 'bg-orange-100 text-orange-800',
      unsatisfactory: 'bg-red-100 text-red-800'
    };
    return colors[rating] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const canStartSelfAssessment = (review) => {
    return review.reviewee_id === user?.id && 
           (review.status === 'not_started' || review.status === 'self_assessment_pending');
  };

  const canStartManagerReview = (review) => {
    return hasAnyRole(['manager', 'hr', 'admin']) &&
           (review.status === 'manager_review_pending' || review.status === 'peer_feedback_pending');
  };

  const canViewReview = (review) => {
    return review.reviewee_id === user?.id || 
           review.reviewer_id === user?.id ||
           hasAnyRole(['hr', 'admin']);
  };

  if (showForm) {
    return (
      <PerformanceReviewForm
        review={currentReview}
        type={formType}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Performance Reviews</h1>
            <p className="text-gray-600 mt-2">Manage your performance reviews and evaluations</p>
          </div>
          {hasAnyRole(['hr', 'admin', 'manager']) && (
            <button
              onClick={() => {/* Navigate to create review */}}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Create Review
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Cycle Filter */}
            <div>
              <label htmlFor="cycle_id" className="block text-sm font-medium text-gray-700">
                Review Cycle
              </label>
              <select
                id="cycle_id"
                value={filters.cycle_id}
                onChange={(e) => handleFilterChange('cycle_id', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Cycles</option>
                {reviewCycles.map(cycle => (
                  <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Statuses</option>
                <option value="not_started">Not Started</option>
                <option value="self_assessment_pending">Self-Assessment Pending</option>
                <option value="manager_review_pending">Manager Review Pending</option>
                <option value="peer_feedback_pending">Peer Feedback Pending</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* My Reviews Filter */}
            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.my_reviews}
                  onChange={(e) => handleFilterChange('my_reviews', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">My Reviews Only</span>
              </label>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <LoadingSpinner size="lg" text="Loading performance reviews..." />
        ) : performanceReviews.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No performance reviews found</h3>
            <p className="text-gray-500">No reviews match your current filters.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {performanceReviews.filter(canViewReview).map(review => {
              const cycle = reviewCycles.find(c => c.id === review.cycle_id);
              
              return (
                <div key={review.id} className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-medium text-gray-900">
                            {cycle?.name || 'Review'}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(review.status)}`}>
                            {review.status.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          {review.overall_rating && (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRatingColor(review.overall_rating)}`}>
                              {review.overall_rating.replace(/_/g, ' ').toUpperCase()}
                            </span>
                          )}
                        </div>
                        
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Type:</span> {cycle?.review_type?.replace(/_/g, ' ') || 'N/A'}
                          </div>
                          <div>
                            <span className="font-medium">Created:</span> {formatDate(review.created_at)}
                          </div>
                          {review.self_assessment_submitted_at && (
                            <div>
                              <span className="font-medium">Self-Assessment:</span> {formatDate(review.self_assessment_submitted_at)}
                            </div>
                          )}
                          {review.manager_review_submitted_at && (
                            <div>
                              <span className="font-medium">Manager Review:</span> {formatDate(review.manager_review_submitted_at)}
                            </div>
                          )}
                        </div>
                        
                        {/* Progress Summary */}
                        {review.overall_score && (
                          <div className="mt-3">
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                              <span>Overall Score</span>
                              <span>{Math.round(review.overall_score * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${review.overall_score * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        )}

                        {/* Linked Goals/OKRs Summary */}
                        {(review.linked_goals?.length > 0 || review.linked_objectives?.length > 0) && (
                          <div className="mt-3 text-sm text-gray-600">
                            <span className="font-medium">Linked:</span>
                            {review.linked_goals?.length > 0 && (
                              <span className="ml-2">{review.linked_goals.length} Goals</span>
                            )}
                            {review.linked_objectives?.length > 0 && (
                              <span className="ml-2">{review.linked_objectives.length} OKRs</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2">
                        {canStartSelfAssessment(review) && (
                          <button
                            onClick={() => handleStartSelfAssessment(review)}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm font-medium"
                          >
                            Start Self-Assessment
                          </button>
                        )}
                        {canStartManagerReview(review) && (
                          <button
                            onClick={() => handleStartManagerReview(review)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium"
                          >
                            Start Manager Review
                          </button>
                        )}
                        <button
                          onClick={() => {/* Navigate to review details */}}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceReviewList;